import React from 'react';
import { WebsiteDesignProps } from './types';
import { Microscope, Dna, FlaskConical } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ResearchLab: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Advanced Research Institute";

    return (
        <div className="bg-white text-slate-800 min-h-screen font-sans">
            <nav className="flex items-center justify-between p-6 border-b border-gray-100">
                <div className="flex items-center gap-2 text-blue-600 font-medium text-xl tracking-tight">
                    <Dna /> {schoolName}
                </div>
                <div className="hidden md:flex gap-6 text-sm text-slate-500 font-medium">
                    <a href="#" className="hover:text-blue-600">Publications</a>
                    <a href="#" className="hover:text-blue-600">Fellowships</a>
                    <a href="#" className="hover:text-blue-600">Labs</a>
                    <a href="#" className="hover:text-blue-600">Events</a>
                </div>
                <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                    Partner With Us
                </Button>
            </nav>

            <main className="container mx-auto px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                            <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
                            Breakthrough Discovered
                        </div>
                        <h1 className="text-5xl md:text-6xl font-light text-slate-900 mb-8 leading-tight">
                            Decoding the <br/> <strong className="font-bold text-blue-600">building blocks</strong> of life.
                        </h1>
                        <p className="text-lg text-slate-500 mb-10 leading-relaxed font-light">
                            We are a community of curious minds dedicated to pushing the boundaries of scientific knowledge through rigorous experimentation and collaboration.
                        </p>
                        
                        <div className="grid grid-cols-2 gap-6 mb-10">
                            <div className="border-l-2 border-blue-200 pl-4">
                                <div className="text-3xl font-bold text-slate-900">450+</div>
                                <div className="text-sm text-slate-500">Active Papers</div>
                            </div>
                            <div className="border-l-2 border-blue-200 pl-4">
                                <div className="text-3xl font-bold text-slate-900">$24M</div>
                                <div className="text-sm text-slate-500">Grant Funding</div>
                            </div>
                        </div>

                        <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-12 font-medium shadow-lg shadow-blue-200">
                            Apply for Residency
                        </Button>
                    </div>

                    <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 relative overflow-hidden">
                        {/* Abstract Data Viz */}
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <FlaskConical size={200} />
                        </div>
                        
                        <div className="relative z-10 space-y-6">
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-start gap-4">
                                <div className="bg-purple-100 p-3 rounded-lg text-purple-600">
                                    <Microscope size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900">Genomics Core</h3>
                                    <p className="text-sm text-slate-500 mt-1">Sequencing and analysis services for molecular biology research.</p>
                                </div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-start gap-4">
                                <div className="bg-emerald-100 p-3 rounded-lg text-emerald-600">
                                    <FlaskConical size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900">Chemical Synthesis</h3>
                                    <p className="text-sm text-slate-500 mt-1">Developing new compounds for pharmaceutical applications.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ResearchLab;
